import { useEffect, useState, ReactNode, useRef } from "react";
import { apiRequest } from "@/api/client";
import { config } from "@/config";
import { AuthContext, User, Role } from "@/auth/AuthContext";
import { setPlatformAuth } from "@/auth/shellAuth";
import {
  decodeJWT,
  getStoredTokens,
  clearStoredTokens,
  hasValidTokens,
  storeTokens,
  getTimeUntilExpiry,
  type CognitoIdToken,
  type CognitoTokens,
} from "@/auth/cognitoTokens";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = !!user;
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  async function refreshAccessToken(): Promise<boolean> {
    const tokens = getStoredTokens();
    if (!tokens || !tokens.refreshToken) {
      console.warn("No refresh token available");
      return false;
    }

    try {
      console.log("Refreshing access token...");
      const response = await fetch(`${config.apiBaseUrl}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      });

      if (!response.ok) {
        console.error("Token refresh failed:", response.status);
        return false;
      }

      const data = await response.json();

      const newTokens: CognitoTokens = {
        idToken: data.id_token,
        accessToken: data.access_token,
        refreshToken: data.refresh_token || tokens.refreshToken,
        expiresIn: data.expires_in,
      };

      storeTokens(newTokens);
      console.log("Token refresh successful");
      return true;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return false;
    }
  }

  function scheduleTokenRefresh() {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    const timeUntilExpiry = getTimeUntilExpiry();
    if (timeUntilExpiry === null) {
      console.log("No valid token, not scheduling refresh");
      return;
    }

    const REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    const refreshIn = Math.max(0, timeUntilExpiry - REFRESH_THRESHOLD);

    console.log(`Scheduling token refresh in ${Math.round(refreshIn / 1000)}s`);

    refreshTimeoutRef.current = setTimeout(async () => {
      const success = await refreshAccessToken();
      if (success) {
        scheduleTokenRefresh();
      } else {
        console.warn("Token refresh failed - logging out");
        logout();
      }
    }, refreshIn);
  }

  async function loadUserFromTokens(providedTokens?: { idToken: string; accessToken: string }) {
    try {
      const tokens = providedTokens || getStoredTokens();

      if (!tokens || !tokens.idToken) {
        console.log("No id_token available");
        setUser(null);
        setLoading(false);
        return;
      }

      const idTokenData = decodeJWT<CognitoIdToken>(tokens.idToken);
      if (!idTokenData) {
        console.error("Failed to decode id_token");
        setUser(null);
        setLoading(false);
        return;
      }

      // Fetch user info from backend (includes roles from DynamoDB)
      try {
        const response = await apiRequest<{
          user: {
            id: string;
            username: string;
            email: string;
            roles: Role[];
            roles_loaded?: boolean;
          };
        }>("/api/user/info");

        console.log("Loaded roles from backend:", response.user.roles);

        const user: User = {
          id: response.user.id,
          username: response.user.username,
          email: response.user.email,
          roles: response.user.roles,
          roles_loaded: response.user.roles_loaded,
        };

        setUser(user);
      } catch (error) {
        // Fall back to user without roles if API fails
        console.error("Failed to fetch user info from backend:", error);

        const user: User = {
          id: idTokenData.sub,
          username: idTokenData.username || idTokenData["cognito:username"],
          email: idTokenData.email?.toLowerCase() ?? "",
          roles: [],
          roles_loaded: false,
        };

        setUser(user);
      }

      scheduleTokenRefresh();
    } catch (err) {
      console.error("Error loading user from tokens:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function loginWithCognitoTokens(tokens: { idToken: string; accessToken: string }) {
    console.log("Logging in with Cognito tokens");
    await loadUserFromTokens(tokens);
  }

  useEffect(() => {
    if (hasValidTokens()) {
      loadUserFromTokens();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function logout() {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    setUser(null);
    clearStoredTokens();

    if (config.cognitoDomain && config.cognitoClientId) {
      const logoutUri = encodeURIComponent(window.location.origin + "/login");
      window.location.href = `https://${config.cognitoDomain}/logout?client_id=${config.cognitoClientId}&logout_uri=${logoutUri}`;
    }
  }

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Publish auth state to window global so federated child apps can consume it
  useEffect(() => {
    setPlatformAuth({
      user,
      loading,
      isAuthenticated,
      logout,
    });
  }, [user, loading, isAuthenticated, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        loginWithCognitoTokens,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
